import { Button, Flex, Spacing, Text } from '@billy/design-system'
import { ReactElement, useCallback } from 'react'
import { useTranslation } from 'react-i18next'

import { useFecImportSessionContext } from '../../contexts/fecImportSessionContext'
import { FecImportAnalysingStep } from '../FecImportAnalysingStep'
import { FecImportConfirmationStep } from '../FecImportConfirmationStep'
import { FecImportFileListSkeleton } from '../FecImportFileListSkeleton'
import { FecImportFinaliseStep } from '../FecImportFinaliseStep'
import { FecImportMappingStep } from '../FecImportMappingStep'
import { FecImportResumeStep } from '../FecImportResumeStep'
import { FecImportSuccessStep } from '../FecImportSuccessStep'
import { FecImportUploadStep } from '../FecImportUploadStep'

interface FecImportWizardContentProps {
  isFinalisingMappings: boolean
  isOrganizationFeatureLoading: boolean
  onConfirmSuccess: () => void
  onFinaliseSetup: () => void
  onModalClose: () => void
  onReviewMappings: () => void
}

export const FecImportWizardContent = ({
  isFinalisingMappings,
  isOrganizationFeatureLoading,
  onConfirmSuccess,
  onFinaliseSetup,
  onModalClose,
  onReviewMappings,
}: FecImportWizardContentProps): ReactElement => {
  const { t } = useTranslation()
  const { isMappingsConfirmed, refetch, resumableFecImports, step } = useFecImportSessionContext()

  const handleRetryButtonClick = useCallback(() => {
    refetch()
  }, [refetch])

  if (isOrganizationFeatureLoading) {
    return <FecImportFileListSkeleton />
  }

  if (isMappingsConfirmed) {
    return <FecImportSuccessStep onClose={onModalClose} />
  }

  if (step === 'restoring') {
    return <FecImportFileListSkeleton />
  }

  if (step === 'load-error') {
    return (
      <Flex flexDirection="column" gap={Spacing.S16}>
        <Text colorVariant="secondary" variant="bodyMD">
          {t('settings.organization.accounting.fec_file_import.modal.load_error')}
        </Text>
        <Flex justifyContent="flex-end">
          <Button onClick={handleRetryButtonClick} variant="filled">
            {t('settings.organization.accounting.fec_file_import.modal.retry_button')}
          </Button>
        </Flex>
      </Flex>
    )
  }

  if (isFinalisingMappings) {
    return <FecImportFinaliseStep onConfirmSuccess={onConfirmSuccess} onReviewMappings={onReviewMappings} />
  }

  if (step === 'mapping') {
    return <FecImportMappingStep onFinaliseSetup={onFinaliseSetup} />
  }

  if (step === 'confirmation') {
    return <FecImportConfirmationStep />
  }

  if (step === 'analysing') {
    return <FecImportAnalysingStep onClose={onModalClose} />
  }

  if (step === 'resume') {
    return <FecImportResumeStep fecImports={resumableFecImports} />
  }

  return <FecImportUploadStep />
}
